import { Component, ChangeDetectionStrategy, Input, OnInit, HostListener, Output, EventEmitter } from '@angular/core';
import { ConfigurationKey } from 'src/app/core/models/configuration-key';
import { InstanceService } from '@commonspirit/ngx-instance';
import { navigateFAD, ProviderFromFAD } from 'src/app/shared/models/v2/search';
import { EnvironmentService } from '@commonspirit/ngx-environment';
import { getEmbedSource } from 'src/app/core/util/root-element';
import '../../../../core/util/string-prototypes';
import { EnvValue } from '@commonspirit/ngx-environment/lib/models/key-value-pair';

@Component({
  selector: 'fal-provider-card-container',
  templateUrl: './provider-card-container.component.html',
  styleUrls: ['./provider-card-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProviderCardContainerComponent implements OnInit {
  @Input() locationName!: string;
  @Input() locationId!: string | number;
  @Output() ProviderNavigateFAD = new EventEmitter<navigateFAD>();
  maxItems!: { max: string } | undefined;
  @Input() providers: ProviderFromFAD[];
  @Input() isHospitalBased: boolean;

  pageSize = 1;
  sliceValue = 0;
  pageIndex = 0;
  hideProviders = false;
  noLinkWithProvider = false;
  conditionallyViewProviders: { start: string; end: string; max: string } | undefined;
  suggestedProviderCards: ProviderFromFAD[] = [];
  startEnd: { start: string; end: string } | undefined;
  isSliderRequired = true;
  enableBlockIT = false;

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.updateConditionalProviders();
  }

  ngOnInit(): void {
    this.updateConditionalProviders();
  }

  constructor(public instanceService: InstanceService, public environmentService: EnvironmentService) {
    const enableBlockITForEnvironment: EnvValue | undefined = this.environmentService.getSetting('enableBlockIT')?.value;
    if (typeof enableBlockITForEnvironment === 'boolean') {
      this.enableBlockIT = enableBlockITForEnvironment;
    }
  }

  get totalPages(): number {
    const ttlpge = Math.ceil(this.providers.length / this.pageSize);
    if (!isNaN(+ttlpge)) {
      return +ttlpge;
    }
    return 0;
  }

  get providerCardCss(): string {
    if (((window.innerWidth > 767 && window.innerWidth < 1030) || window.innerWidth < 766) && this.suggestedProviderCards.length <= 2) {
      return 'spaceEvenly';
    }
    return '';
  }

  getPaginatorData(event: number): void {
    this.pageIndex = event;
    this.sliceValue = this.pageIndex * this.pageSize;
    this.updateSuggestedProviderCards();
  }

  updateSuggestedProviderCards(): void {
    this.suggestedProviderCards = this.getSuggestedProviderCards();
  }

  updateConditionalProviders(): void {
    this.conditionallyViewProviders = this.getConfigValuesToShowOrHideProviders(ConfigurationKey.ConditionallyViewProviders);

    switch (this.providers.length > 0) {
      case this.providers.length >= +this.conditionallyViewProviders?.max!:
        this.hideProviders = true;
        break;
      case this.providers.length < +this.conditionallyViewProviders?.start!:
        this.noLinkWithProvider = true;
        this.updateSuggestedProviderCards();
        break;
      default:
        this.updateSuggestedProviderCards();
        break;
    }
  }

  fadLocationNavigate(): void {
    const embedSource = getEmbedSource() ?? '';
    const fadPageUrl = this.getSourceInstanceConfiguration(ConfigurationKey.FadPage, embedSource)?.value as { link: string };
    const redirectURL = `${fadPageUrl.link}${this.formatFadURL(this.locationId.toString())}`;
    sessionStorage.setItem('toFAD', 'true'); //need it to go back to location results.
    window.open(redirectURL, '_self');
  }

  getSourceInstanceConfiguration(key: string, source: string): { embedSource: string; value: EnvValue } | undefined {
    const returnValue =
      (this.instanceService.getConfiguration(key)?.value as {
        embedSource: string;
        value: EnvValue;
      }[]) ?? [];
    return returnValue.find((x) => x.embedSource === source);
  }

  formatFadURL(locationId: string): string {
    const hospitalBased = this.isHospitalBased ? 'isHospitalBased=true' : 'isHospitalBased=false';
    if (this.enableBlockIT) {
      return `/l/location-${locationId}?FALredirecttoFAD=true&${hospitalBased}&FALlocation=${locationId}`;
    } else {
      return `/l/location-${locationId}?FALredirecttoFAD=true&${hospitalBased}&FALlocation=${this.locationName
        ?.toUrlFriendly()
        ?.stripPeriod()
        ?.condenseDashes()
        .removeSpecialCharacters()
        .sanitizeHyphens()}-${locationId}`;
    }
  }

  getConfigValuesToShowOrHideProviders(key: string): { start: string; end: string; max: string } | undefined {
    const value = (this.instanceService.getConfiguration(key)?.value as { start: string; end: string; max: string }) ?? undefined;
    return value;
  }

  getSuggestedProviderCards(): ProviderFromFAD[] {
    const desiredPageSize = window.innerWidth < 766 ? 1 : window.innerWidth > 767 && window.innerWidth < 1030 ? 2 : 3;
    // Reset the user back to the first Page when changing ViewPort
    if (this.pageSize !== desiredPageSize) {
      this.pageIndex = 0;
      this.sliceValue = 0;
    }

    this.pageSize = desiredPageSize;

    switch (desiredPageSize > 0) {
      case this.providers.length <= 3 && desiredPageSize == 3:
        this.isSliderRequired = false;
        break;
      case this.providers.length <= 2 && desiredPageSize == 2:
        this.isSliderRequired = false;
        break;
      case this.providers.length <= 1 && desiredPageSize == 1:
        this.isSliderRequired = false;
        break;
      default:
        return this.providers.slice(this.sliceValue, this.sliceValue + this.pageSize);
    }
    return this.providers;
  }

  handleProviderNavigateFAD(event: navigateFAD): void {
    this.ProviderNavigateFAD.emit(event);
  }
}
